import Button from '@rio-cloud/rio-uikit/Button';
import classnames from 'classnames';
import { Component } from 'react';
import { FormattedMessage } from 'react-intl';

import { UnreachableCaseError } from '../../../../utils/typescriptUtil';
import { PaymentMethodType } from '../../api/paymentMethods/paymentMethodTypes.types';
import { safeDataLayerPush } from '../utils/googleTagManagerWrapper';
import { BankTransferPayment } from './BankTransferPayment';
import { PaymentInformation } from './PaymentInformation';
import { getGAPaymentType } from './paymentMethodsGTMUtils';
import type { PaymentMethod } from './redux/types';
import { StripePaymentFormContainer } from './StripePaymentFormContainer';

function pushToGA({ paymentType }: { paymentType?: PaymentMethodType } = {}) {
    if (paymentType) {
        const createEvent = () => ({
            event: 'PaymentMethodSelected',
            eventPayload: {
                paymentType: getGAPaymentType(paymentType),
            },
        });
        safeDataLayerPush(createEvent);
    }
}

interface Props {
    paymentInformation?: PaymentMethod;
    paymentMethodTypes: PaymentMethodType[];
    setEditMode: (isEditMode: boolean) => void;
    isEditPayment: boolean;
}

type OwnState = { paymentMethodType?: PaymentMethodType; isSubmittingToStripe: boolean };

function disableOrEmpty(disableNoneActiveTabs: boolean) {
    return disableNoneActiveTabs ? 'disabled' : '';
}

export class PaymentMethodPage extends Component<Props, OwnState> {
    constructor(props: Props) {
        super(props);
        if (props.paymentInformation) {
            this.state = {
                paymentMethodType: props.paymentInformation.paymentType,
                isSubmittingToStripe: false,
            };
        } else {
            this.state = {
                paymentMethodType: PaymentMethodType.CREDIT_CARD,
                isSubmittingToStripe: false,
            };
            this.props.setEditMode(true);
        }
        this.setSubmittingToStripe = this.setSubmittingToStripe.bind(this);
    }

    componentWillUnmount() {
        this.props.setEditMode(false);
    }

    setSubmittingToStripe(isSubmittingToStripe: boolean) {
        this.setState({ isSubmittingToStripe });
    }

    setPaymentMethodType(paymentMethodType: PaymentMethodType) {
        pushToGA({ paymentType: paymentMethodType });
        this.setState({ paymentMethodType });
    }

    handleShowCurrentPaymentMethod() {
        this.props.setEditMode(false);
        this.setState({
            paymentMethodType: this.props.paymentInformation?.paymentType,
        });
    }

    renderPaymentForm(paymentMethod?: PaymentMethodType, paymentInformation?: PaymentMethod) {
        if (paymentMethod === PaymentMethodType.BANK_TRANSFER) {
            return (
                <div className='max-width-700'>
                    <BankTransferPayment />
                </div>
            );
        }
        return (
            <div className='max-width-700'>
                <StripePaymentFormContainer
                    paymentMethod={paymentMethod}
                    paymentInformation={paymentInformation}
                    setSubmittingToStripe={this.setSubmittingToStripe}
                />
            </div>
        );
    }

    renderPaymentInformation(paymentInformation?: PaymentMethod) {
        return (
            <div>
                <h4>
                    <FormattedMessage id='marketplace.payment.information.header.current' />
                </h4>
                <PaymentInformation paymentInformation={paymentInformation} />
            </div>
        );
    }

    renderPaymentTabs({
        paymentMethodType,
        disableNoneActiveTabs,
        paymentMethodTypes,
    }: {
        paymentMethodType?: PaymentMethodType;
        disableNoneActiveTabs: boolean;
        paymentMethodTypes: PaymentMethodType[];
    }) {
        const paymentTypeProperties1 = paymentMethodTypes.map(type => {
            switch (type) {
                case PaymentMethodType.SEPA:
                    return {
                        type,
                        className: 'tabSepa cursor-pointer',
                        headerId: 'marketplace.payment.method.sepa',
                    };
                case PaymentMethodType.CREDIT_CARD:
                    return {
                        type,
                        className: 'tabCreditCard cursor-pointer',
                        headerId: 'marketplace.payment.method.creditCard',
                    };
                case PaymentMethodType.BANK_TRANSFER:
                    return {
                        type,
                        className: 'tabBankTransfer cursor-pointer',
                        headerId: 'marketplace.payment.method.bankTransfer',
                    };
                default:
                    throw new UnreachableCaseError(type);
            }
        });

        return (
            <ul className='nav nav-tabs flex-wrap'>
                {paymentTypeProperties1.map((it, index) => {
                    return (
                        <li
                            // biome-ignore lint/suspicious/noArrayIndexKey: Payment types array is stable and derived from enum values
                            key={index}
                            className={`${it.className} ${
                                paymentMethodType === it.type ? 'active' : disableOrEmpty(disableNoneActiveTabs)
                            }`}
                            role='presentation'
                        >
                            <a onClick={() => !disableNoneActiveTabs && this.setPaymentMethodType(it.type)}>
                                <FormattedMessage id={it.headerId} />
                            </a>
                        </li>
                    );
                })}
                <div className='display-flex justify-content-end flex-1-1'>
                    {this.props.isEditPayment ? this.renderShowCurrentPaymentButton() : this.renderEditPaymentButton()}
                </div>
            </ul>
        );
    }

    renderEditPaymentButton() {
        return (
            <Button
                key='editPayment'
                className={'changePayment btn btn-default'}
                onClick={() => this.props.setEditMode(true)}
                autoTrackingKey='marketplace.payment.button.changePayment'
            >
                <FormattedMessage id='marketplace.payment.button.changePayment' />
            </Button>
        );
    }

    renderShowCurrentPaymentButton() {
        if (!this.props.paymentInformation) {
            return null;
        }
        const disabled = this.state.isSubmittingToStripe;

        return (
            <Button
                key='showCurrentPayment'
                className={classnames('showCurrentPayment', 'btn', 'btn-default', disabled && 'disabled')}
                onClick={() => !disabled && this.handleShowCurrentPaymentMethod()}
                autoTrackingKey='marketplace.payment.button.showCurrentPayment'
            >
                <span className='rioglyph rioglyph-revert' />
                <FormattedMessage id='marketplace.payment.button.showCurrentPayment' />
            </Button>
        );
    }

    renderPaymentMethodPageBody({
        paymentInformation,
        paymentMethodType,
        paymentMethodTypes,
    }: Pick<Props, 'paymentInformation' | 'paymentMethodTypes'> & { paymentMethodType?: PaymentMethodType }) {
        const paymentContent = this.props.isEditPayment
            ? this.renderPaymentForm(paymentMethodType, paymentInformation)
            : this.renderPaymentInformation(paymentInformation);

        const disableNoneActiveTabs = !!paymentInformation && !this.props.isEditPayment;

        return (
            <div>
                {this.renderPaymentTabs({
                    paymentMethodType,
                    disableNoneActiveTabs,
                    paymentMethodTypes,
                })}
                <div className='tab-content padding-top-25 display-flex flex-column align-items-center'>
                    {paymentContent}
                </div>
            </div>
        );
    }

    render() {
        return (
            <div className='PaymentMethodPage'>
                {this.renderPaymentMethodPageBody({
                    paymentInformation: this.props.paymentInformation,
                    paymentMethodType: this.state.paymentMethodType,
                    paymentMethodTypes: this.props.paymentMethodTypes,
                })}
            </div>
        );
    }
}
